import { WORLD } from '../config/world';
import { EYE, alongAzimuth } from './composition';
import type { Vec3 } from './frame';

export interface RigPose {
  position: Vec3;
  target: Vec3;
  /** Vertical field of view, degrees. */
  fovDeg: number;
}

const LANDSCAPE_PITCH_DEG = 1.9;
const PORTRAIT_PITCH_DEG = 10.2;
const tanDeg = (d: number): number => Math.tan((d * Math.PI) / 180);

/** Looks along -Z (azimuth 250°), pitched up so the horizon sits at ~55 % from the top (sky ≥ 50 %). */
export const LANDSCAPE_RIG: RigPose = {
  position: EYE,
  target: [EYE[0], EYE[1] + 20 * tanDeg(LANDSCAPE_PITCH_DEG), EYE[2] - 20],
  fovDeg: 36,
};

/** 5 m closer along the torii ray (keeps the sun/island alignment), centred on the torii, horizon at ~65 %. */
export const PORTRAIT_RIG: RigPose = {
  position: alongAzimuth(WORLD.toriiAzimuthDeg, 5, EYE[1]),
  target: alongAzimuth(WORLD.toriiAzimuthDeg, 20, EYE[1] + 15 * tanDeg(PORTRAIT_PITCH_DEG)),
  fovDeg: 62,
};

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerp3 = (a: Vec3, b: Vec3, t: number): Vec3 => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

export function blendRig(aspect: number): RigPose {
  if (!Number.isFinite(aspect)) return LANDSCAPE_RIG;
  const t = smoothstep(0.75, 1.5, aspect);
  if (t === 1) return LANDSCAPE_RIG;
  if (t === 0) return PORTRAIT_RIG;
  return {
    position: lerp3(PORTRAIT_RIG.position, LANDSCAPE_RIG.position, t),
    target: lerp3(PORTRAIT_RIG.target, LANDSCAPE_RIG.target, t),
    fovDeg: lerp(PORTRAIT_RIG.fovDeg, LANDSCAPE_RIG.fovDeg, t),
  };
}
